import { Trash2, Save } from "lucide-react";
import DataTable from "../../../components/table";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import api from "../../../services/axios";
import { Loader2 } from "lucide-react";
import { Tooltip } from "../../../components/Tooltip";

// MultiSelectDropdown component
function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder,
  readOnly,
  label,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const ref = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  
  // Filter options based on search query
  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const allSelected = filteredOptions.length > 0 && 
    filteredOptions.every(opt => selected.includes(opt));

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
      // Prevent body scroll when dropdown is open
      document.body.style.overflow = "hidden";
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      // Re-enable body scroll when dropdown is closed
      document.body.style.overflow = "auto";
      // Reset search when closing
      setSearchQuery("");
    }
  }, [open]);

  const toggleAll = () => {
    if (readOnly) return;
    if (allSelected) {
      // Deselect all filtered options
      onChange(selected.filter(v => !filteredOptions.includes(v)));
    } else {
      // Select all filtered options
      const newSelected = [...selected];
      filteredOptions.forEach(opt => {
        if (!newSelected.includes(opt)) {
          newSelected.push(opt);
        }
      });
      onChange(newSelected);
    }
  };

  const toggleOption = (value) => {
    if (readOnly) return;
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div ref={ref} className="relative w-full">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        {`${selected.length} selected`}
      </div>

      <div
        onClick={() => !readOnly && setOpen(!open)}
        className={`border border-gray-300 dark:border-gray-600 rounded px-2 py-1 cursor-pointer bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 
          ${
            readOnly
              ? "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700"
              : ""
          }`}
        style={{
          display: "-webkit-box",
          WebkitLineClamp: "3",
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minHeight: "24px", // Ensures minimum height even when empty
          maxWidth: "400px", // Add this line
        }}
      >
        {selected.length ? selected.join(", ") : placeholder}
      </div>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={() => setOpen(false)}
          />
          {/* Dropdown */}
          <div
            className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg max-h-[300px] flex flex-col"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200 dark:border-gray-600 sticky top-0 bg-white dark:bg-gray-800">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search ${label || 'options'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Options List */}
            <div className="overflow-y-auto max-h-[240px]">
              {/* Select All option */}
              {filteredOptions.length > 0 && (
                <label
                  className={`flex items-center px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 ${
                    readOnly ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mr-2 accent-blue-600"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={readOnly}
                  />
                  <span className="dark:text-gray-300 font-semibold">
                    Select All {filteredOptions.length !== options.length ? `(${filteredOptions.length})` : ''}
                  </span>
                </label>
              )}

              {/* Individual options */}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt, i) => (
                  <label
                    key={i}
                    className={`flex items-center px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      readOnly ? "pointer-events-none opacity-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mr-2 accent-blue-600"
                      checked={selected.includes(opt)}
                      onChange={() => toggleOption(opt)}
                      disabled={readOnly}
                    />
                    <span className="dark:text-gray-300">{opt}</span>
                  </label>
                ))
              ) : (
                <div className="px-2 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No options found
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// SearchableSelectDropdown component for single selection (like Tenant)
function SearchableSelectDropdown({
  options,
  value,
  onChange,
  placeholder,
  readOnly,
  getOptionLabel,
  getOptionValue,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const ref = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Filter options based on search query
  const filteredOptions = options.filter(opt => {
    const label = getOptionLabel ? getOptionLabel(opt) : opt;
    return label?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Get selected option label
  const selectedOption = options.find(opt => {
    const optValue = getOptionValue ? getOptionValue(opt) : opt;
    return String(optValue) === String(value);
  });
  const selectedLabel = selectedOption 
    ? (getOptionLabel ? getOptionLabel(selectedOption) : selectedOption)
    : placeholder;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
      // Prevent body scroll when dropdown is open
      document.body.style.overflow = "hidden";
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      // Re-enable body scroll when dropdown is closed
      document.body.style.overflow = "auto";
      // Reset search when closing
      setSearchQuery("");
    }
  }, [open]);

  const handleSelect = (opt) => {
    if (readOnly) return;
    const optValue = getOptionValue ? getOptionValue(opt) : opt;
    onChange(optValue);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative w-full">
      <div
        onClick={() => !readOnly && setOpen(!open)}
        className={`border border-gray-300 dark:border-gray-600 rounded px-2 py-1 cursor-pointer bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 
          ${
            readOnly
              ? "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700"
              : ""
          }`}
        style={{
          minHeight: "24px",
          maxWidth: "400px",
        }}
      >
        {selectedLabel}
      </div>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={() => setOpen(false)}
          />
          {/* Dropdown */}
          <div
            className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg max-h-[300px] flex flex-col"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200 dark:border-gray-600 sticky top-0 bg-white dark:bg-gray-800">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Options List */}
            <div className="overflow-y-auto max-h-[240px]">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt, i) => {
                  const optValue = getOptionValue ? getOptionValue(opt) : opt;
                  const optLabel = getOptionLabel ? getOptionLabel(opt) : opt;
                  const isSelected = String(optValue) === String(value);
                  
                  return (
                    <div
                      key={i}
                      onClick={() => handleSelect(opt)}
                      className={`px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        readOnly ? "pointer-events-none opacity-50" : ""
                      } ${isSelected ? "bg-blue-50 dark:bg-blue-900/20" : ""}`}
                    >
                      <span className="dark:text-gray-300">{optLabel}</span>
                    </div>
                  );
                })
              ) : (
                <div className="px-2 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No options found
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// First, add this modified MultiSelectDropdown component for measurements
function MeasurementDropdown({
  options,
  selected,
  onChange,
  placeholder,
  readOnly,
  showOnlyUserDefined,
}) {
  const [open, setOpen] = useState(false);
  const [showUserDefined, setShowUserDefined] = useState(
    showOnlyUserDefined || false
  );
  const [searchQuery, setSearchQuery] = useState("");
  const ref = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Filter options based on user_defined_title if checkbox is checked
  const userDefinedFilteredOptions = showUserDefined
    ? options.filter((opt) => opt.user_defined_title)
    : options;
  
  // Filter options based on search query
  const filteredOptions = userDefinedFilteredOptions.filter(opt => {
    const displayName = showUserDefined ? opt.user_defined_title : opt.name;
    return displayName?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Check if all filtered options are selected
  const allSelected = filteredOptions.length > 0 && 
    filteredOptions.every(opt => selected.includes(opt.name));

  // Toggle select all functionality
  const toggleSelectAll = () => {
    if (readOnly) return;
    if (allSelected) {
      // Deselect all filtered options
      const filteredOptionNames = filteredOptions.map(opt => opt.name);
      onChange(selected.filter(v => !filteredOptionNames.includes(v)));
    } else {
      // Select all filtered options
      const filteredOptionNames = filteredOptions.map(opt => opt.name);
      const newSelected = [...selected];
      filteredOptionNames.forEach(name => {
        if (!newSelected.includes(name)) {
          newSelected.push(name);
        }
      });
      onChange(newSelected);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      // Reset search when closing
      setSearchQuery("");
    }
  }, [open]);

  return (
    <div ref={ref} className="relative w-full">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {`${selected.length} selected`}
        </div>
        <label className="flex items-center text-xs text-gray-500 dark:text-gray-400">
          <input
            type="checkbox"
            className="mr-1 accent-blue-600"
            checked={showUserDefined}
            onChange={(e) => setShowUserDefined(e.target.checked)}
          />
          User Defined Only
        </label>
      </div>

      <div
        onClick={() => !readOnly && setOpen(!open)}
        className={`border border-gray-300 dark:border-gray-600 rounded px-2 py-1 cursor-pointer bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 
          ${
            readOnly
              ? "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700"
              : ""
          }`}
        style={{
          display: "-webkit-box",
          WebkitLineClamp: "3",
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minHeight: "24px", // Ensures minimum height even when empty
          maxWidth: "400px",
        }}
      >
        {selected.length ? selected.join(", ") : placeholder}
      </div>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={() => setOpen(false)}
          />
          {/* Dropdown */}
          <div
            className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg max-h-[300px] flex flex-col"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200 dark:border-gray-600 sticky top-0 bg-white dark:bg-gray-800">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search measurements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Options List */}
            <div className="overflow-y-auto max-h-[240px]">
              {/* Select All option */}
              {filteredOptions.length > 0 && (
                <label
                  className={`flex items-center px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 ${
                    readOnly ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mr-2 accent-blue-600"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    disabled={readOnly}
                  />
                  <span className="dark:text-gray-300 font-semibold">
                    Select All {filteredOptions.length !== userDefinedFilteredOptions.length ? `(${filteredOptions.length})` : ''}
                  </span>
                </label>
              )}
              
              {filteredOptions.length ? (
                filteredOptions.map((opt, i) => (
                <label
                  key={i}
                  className={`flex items-center px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    readOnly ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mr-2 accent-blue-600"
                    checked={selected.includes(opt.name)}
                    onChange={() => {
                      if (!readOnly) {
                        const value = opt.name;
                        if (selected.includes(value)) {
                          onChange(selected.filter((v) => v !== value));
                        } else {
                          onChange([...selected, value]);
                        }
                      }
                    }}
                    disabled={readOnly}
                  />
                  <span className="dark:text-gray-300">
                    {showUserDefined ? opt.user_defined_title : opt.name}
                  </span>
                </label>
              ))
            ) : (
              <div className="px-2 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No options found
              </div>
            )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ConfigTable({ tenants, setIsDirty, isDirty }) {
  const [activeTab, setActiveTab] = useState("exclusion-rules");
  const token = localStorage.getItem("origo_access_token");
  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_ORIGO_BASE_URL,
    headers: { Authorization: `Bearer ${token}` },
  });

  const [tableData, setTableData] = useState([]);
  const [originalApiData, setOriginalApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ show: false, row: null });
  const [dropdowns, setDropdowns] = useState({
    tenants: tenants || [],
    stationsFull: [],
    stations: [],
    stationGroups: [],
    measurements: [],
    statuses: ["Any Status","open", "closed"],
    online: ["True", "False"],
  });

  useEffect(() => {
    fetchRules();
  }, [activeTab]);
  useEffect(() => {
    setDropdowns((prev) => ({ ...prev, tenants }));
  }, [tenants]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/config/${activeTab}`);
      const apiData = res.data?.data || [];

      const mappedData = apiData.map((item) => ({
        id: item.id,
        tenant: item.tenant_id,
        tenant_name: item.tenant_name,
        station: item.station_names || [],
        stationGroup: item.station_groups || [],
        sensors: item.sensor_names || [],
        status: item.status || "",
        online: item.online || true,
        isExisting: true,
      }));

      setTableData(mappedData);
      // Store original API data for comparison
      setOriginalApiData(mappedData);

      // ✅ preload dropdowns for each tenant from localStorage
      mappedData.forEach((row) => {
        const cached = localStorage.getItem(`dropdowns_${row.tenant}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          setDropdowns((prev) => ({ ...prev, ...parsed }));
        }
      });
    } catch (err) {
      console.error("Error fetching rules:", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to normalize arrays for comparison
  const normalizeArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    return [...arr].sort();
  };

  // Helper function to compare two rows
  const rowsAreEqual = (row1, row2) => {
    if (!row1 || !row2) return false;
    
    return (
      row1.tenant === row2.tenant &&
      JSON.stringify(normalizeArray(row1.station)) === JSON.stringify(normalizeArray(row2.station)) &&
      JSON.stringify(normalizeArray(row1.stationGroup)) === JSON.stringify(normalizeArray(row2.stationGroup)) &&
      JSON.stringify(normalizeArray(row1.sensors)) === JSON.stringify(normalizeArray(row2.sensors)) &&
      (row1.status || "") === (row2.status || "")
    );
  };

  // Check if current state differs from original API state
  useEffect(() => {
    // Check for new rows (non-existing rows)
    const hasNewRows = tableData.some((r) => !r.isExisting);
    
    if (hasNewRows) {
      setIsDirty(true);
      return;
    }

    // Check if existing rows have been modified
    const existingRows = tableData.filter((r) => r.isExisting);
    const hasModifiedRows = existingRows.some((currentRow) => {
      const originalRow = originalApiData.find((orig) => orig.id === currentRow.id);
      if (!originalRow) return false; // Row was deleted
      return !rowsAreEqual(currentRow, originalRow);
    });

    // Check if any original rows were deleted
    const hasDeletedRows = originalApiData.some((origRow) => {
      return !tableData.find((currentRow) => currentRow.id === origRow.id);
    });

    // Set isDirty only if there are actual changes
    setIsDirty(hasModifiedRows || hasDeletedRows);
  }, [tableData, originalApiData, setIsDirty]);

  const fetchDropdowns = async (tenantId) => {
    if (!tenantId) return;
    try {
      const res = await axiosInstance.post("/config/filter", {
        tenant_id: Number(tenantId) === 0 ? null : Number(tenantId),
      });
      const data = res.data?.data || {};

      const newDropdowns = {
        stationsFull: data.stations || [],
        stations: data?.stations
          ? [...data.stations?.map((s) => s.origo_station_name)]
          : [],
        stationGroups: [...data.stationGroups] || [],
        measurements:
          data.sensors?.map((s) => ({
            name: s.origo_sensor_name,
            user_defined_title: s.origo_admin_defined_title || false,
          })) || [],
        statuses: data.status || ["open", "closed"],
        online: data.online?.map(String) || ["true", "false"],
      };
      console.log(newDropdowns);

      setDropdowns((prev) => ({ ...prev, ...newDropdowns }));

      // ✅ Save in localStorage per tenant
      localStorage.setItem(
        `dropdowns_${tenantId}`,
        JSON.stringify(newDropdowns)
      );
    } catch (err) {
      console.error("Error fetching dropdowns:", err);
    }
  };

  const handleAddRow = () => {
    setTableData((prev) => [
      {
        id: Date.now(),
        tenant: "",
        stationGroup: [],
        station: [],
        sensors: [],
        status: "",
        online: "true",
        isExisting: false,
      },
      ...prev,
    ]);
  };

  const handleSave = async (row) => {
    // Validation checks
    if (!row.tenant) {
      toast.error("Please select a tenant");
      return;
    }

    if (Number(row.tenant) && row.station.length === 0) {
      toast.error("Please select Station");
      return;
    }

    if (row.stationGroup.length === 0) {
      toast.error("Please select Station Group");
      return;
    }

    if (row.sensors.length === 0) {
      toast.error("Please select at least one Measurement");
      return;
    }

    // if (!row.status) {
    //   toast.error("Please select a Status");
    //   return;
    // }

    try {
      const tenantObj = dropdowns.tenants.find(
        (t) => t.id === Number(row.tenant)
      );
      const stationIds = dropdowns.stationsFull
        .filter((s) => row.station.includes(s.origo_station_name))
        .map((s) => s.id);

      const payload = {
        tenant_id: tenantObj?.id || 0,
        tenant_name: tenantObj?.origo_tenant_name || "",
        station_ids: stationIds,
        station_names: row.station,
        station_groups: row.stationGroup,
        sensor_names: row.sensors || [],
        status: row.status,
      };

      const res = await axiosInstance.post(`/config/${activeTab}`, payload);

      const savedRow = {
        id: res.data.data.id,
        tenant: payload.tenant_id,
        tenant_name: tenantObj?.origo_tenant_name,
        station: payload.station_names || [],
        stationGroup: payload.station_groups || [],
        sensors: payload.sensor_names || [],
        status: payload.status || "",
        online: true,
        isExisting: true,
      };

      setTableData((prev) =>
        prev.map((r) =>
          r.id === row.id ? savedRow : r
        )
      );

      // Update originalApiData to reflect the saved state
      setOriginalApiData((prev) => {
        const existingIndex = prev.findIndex((r) => r.id === row.id);
        if (existingIndex >= 0) {
          // Update existing row
          return prev.map((r) => (r.id === row.id ? savedRow : r));
        } else {
          // Add new row
          return [...prev, savedRow];
        }
      });

      toast.success("Rule saved successfully");
    } catch (err) {
      console.error("Save failed:", err.response?.data || err.message);
      toast.error(err.response?.data?.message || "Save failed");
    }
  };

  const handleDelete = async (row) => {
    if (row.isExisting) {
      try {
        await axiosInstance.delete(`/config/${activeTab}`, {
          data: { [`${activeTab.split("-")[0]}_rule_id`]: row.id },
        });
        await fetchRules();
        setDeleteModal({ show: false, row: null });
        toast.success("Rule deleted successfully");
      } catch (err) {
        console.error("Delete failed:", err);
        toast.error(err.response?.data?.message || "Delete failed");
      }
    } else {
      setTableData((prev) => prev.filter((r) => r.id !== row.id));
      setDeleteModal({ show: false, row: null });
    }
  };

  const columns = [
    // Replace the select render code in the Tenant column
    {
      label: "Tenant",
      accessor: "tenant",
      render: (row) => {
        const isDisabled = !dropdowns.tenants.length || row.isExisting;

        return (
          <div className="relative w-full">
            {!dropdowns.tenants.length ? (
              <div className="flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-gray-100 dark:bg-gray-700" style={{ minHeight: "24px", maxWidth: "400px" }}>
                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
              </div>
            ) : (
              <SearchableSelectDropdown
                options={dropdowns.tenants}
                value={row.tenant}
                onChange={async (tenantId) => {
                  if (row.isExisting) return;
                  // Convert to string for consistency (especially for "0" which represents "All Tenants")
                  const tenantValue = String(tenantId);
                  setTableData((prev) =>
                    prev.map((r) =>
                      r.id === row.id
                        ? {
                            ...r,
                            tenant: tenantValue,
                            station: [],
                            stationGroup: [],
                            sensors: [],
                            status: "",
                            online: "True",
                          }
                        : r
                    )
                  );
                  await fetchDropdowns(tenantValue);
                }}
                placeholder="Select Tenant"
                readOnly={isDisabled}
                getOptionValue={(opt) => opt.id}
                getOptionLabel={(opt) => 
                  opt.origo_tenant_id === "0" || opt.origo_tenant_id === 0
                    ? opt.origo_tenant_name
                    : `${opt.origo_tenant_name} (${opt.origo_tenant_id})`
                }
              />
            )}
          </div>
        );
      },
    },
    {
      label: "Station",
      accessor: "station",
      render: (row) => {
        const isAllTenantsSelected = row.tenant === "0" || row.tenant === 0;
        const noTenantSelected = !row.tenant && row.tenant !== 0 && row.tenant !== "0";
        const isDisabled =
          row.isExisting || isAllTenantsSelected || noTenantSelected;

        const tooltipMessage = noTenantSelected ? (
          "Please select a tenant first"
        ) : isAllTenantsSelected ? (
          "Station selection is disabled for All Tenants"
        ) : row.isExisting ? (
          <div>
            <p className="font-semibold mb-1">Selected stations:</p>
            {row.station?.map((s, i) => (
              <div key={i} className="pl-2">
                {i + 1}. {s}
              </div>
            )) || "None"}
          </div>
        ): null

        return (
          <Tooltip message={tooltipMessage} show={!!tooltipMessage}>
            <div className="w-full">
              <MultiSelectDropdown
                options={dropdowns.stations}
                selected={row.station || []}
                onChange={(val) => {
                  if (!isDisabled) {
                    setTableData((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, station: val } : r
                      )
                    );
                  }
                }}
                placeholder={
                  noTenantSelected
                    ? "Select a tenant first"
                    : isAllTenantsSelected
                    ? "Disabled for All Tenants"
                    : "Select Station"
                }
                readOnly={isDisabled}
                label={"Stations"}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      label: "Station Group",
      accessor: "stationGroup",
      render: (row) => {
        const isAllTenantsSelected = row.tenant === "0" || row.tenant === 0;
        const noTenantSelected = !row.tenant && row.tenant !== 0 && row.tenant !== "0";
        const isDisabled = row.isExisting || noTenantSelected;
    
        const tooltipMessage = (isDisabled && row.stationGroup?.length )
          ? <div>
              <p className="font-semibold mb-1">Selected station groups:</p>
              {row.stationGroup?.map((g, i) => (
                <div key={i} className="pl-2">
                  {i + 1}. {g}
                </div>
              ))}
            </div>
          : null;
    
        return (
          <Tooltip message={tooltipMessage} show={!!tooltipMessage}>
            <div className="w-full">
              <MultiSelectDropdown
                options={dropdowns.stationGroups}
                selected={row.stationGroup || []}
                onChange={(val) => {
                  if (!row.isExisting) {
                    setTableData((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, stationGroup: val } : r
                      )
                    );
                  }
                }}
                placeholder="Select Group"
                readOnly={isDisabled}
                label={"Groups"}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      label: "Measurement",
      accessor: "sensors",
      render: (row) => {
        const isAllTenantsSelected = row.tenant === "0" || row.tenant === 0;
        const noTenantSelected = !row.tenant && row.tenant !== 0 && row.tenant !== "0";
        const isDisabled = row.isExisting || noTenantSelected;
    
        const tooltipMessage = (isDisabled && row.sensors?.length)
          ? <div>
              <p className="font-semibold mb-1">Selected measurements:</p>
              {row.sensors?.map((s, i) => (
                <div key={i} className="pl-2">
                  {i + 1}. {s}
                </div>
              ))}
            </div>
          : null;
    
        return (
          <Tooltip message={tooltipMessage} show={!!tooltipMessage}>
            <div className="w-full">
              <MeasurementDropdown
                options={dropdowns.measurements}
                selected={row.sensors || []}
                onChange={(val) => {
                  if (!row.isExisting) {
                    setTableData((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, sensors: val } : r
                      )
                    );
                  }
                }}
                placeholder="Select Sensors"
                readOnly={isDisabled}
              />
            </div>
          </Tooltip>
        );
      },
    },

    {
      label: "Status",
      accessor: "status",
      render: (row) => (
        <select
          value={row.status || ""}
          onChange={(e) => {
            if (!row.isExisting) {
              setTableData((prev) =>
                prev.map((r) =>
                  r.id === row.id ? { ...r, status: e.target.value } : r
                )
              );
            }
          }}
          className={`select select-bordered select-sm w-full text-gray-500 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-600 ${
            row.isExisting ? "cursor-not-allowed bg-gray-100" : ""
          }`}
          style={row.isExisting ? { pointerEvents: "none" } : {}}
        >
          <option value="">Any Status</option>
          {dropdowns.statuses.map((s, i) => (
            <option key={i} value={s==='Any Status' ? '' : s}>
              {s}
            </option>
          ))}
        </select>
      ),
    },
    // {
    //   label: "Online",
    //   accessor: "online",
    //   render: (row) => (
    //     <select
    //       value={row.online || ""}
    //       onChange={e => {
    //         if (!row.isExisting) setTableData(prev => prev.map(r => r.id === row.id ? { ...r, online: e.target.value } : r));
    //       }}
    //       className="select select-bordered select-sm w-full text-gray-500 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-600 "
    //       style={row.isExisting ? { pointerEvents: "none", backgroundColor: "#f5f5f5" } : {}}
    //     >
    //       {dropdowns.online.map((o, i) => <option key={i} value={o}>{o}</option>)}
    //     </select>
    //   ),
    // },
    {
      label: "Action",
      accessor: "action",
      render: (row) => (
        <div className="flex items-center gap-3 justify-center">
          <button
            onClick={() => setDeleteModal({ show: true, row })}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 size={18} />
          </button>

          {row.isExisting ? (
            <div className="relative group w-[18px] h-[18px]">
              <button className="text-gray-400 cursor-not-allowed">
                <Save size={18} />
              </button>
              <div className="absolute bottom-full mb-1 left-1/2 min-w-28 transform -translate-x-1/2 px-2 py-1 text-xs text-white bg-black rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                Cannot update existing rule. Delete first to make changes.
              </div>
            </div>
          ) : (
            <button
              onClick={() => handleSave(row)}
              className="text-blue-600 hover:text-blue-800"
            >
              <Save size={18} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleTabChange = (newTab) => {
    if (isDirty) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Do you really want to switch tabs?"
      );
      if (!confirmLeave) return;
    }
    setIsDirty(false);
    setActiveTab(newTab);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow dark:shadow-gray-900 p-4 h-full">
      <div className="flex justify-between items-center mb-4">
        <div className="flex bg-white dark:bg-gray-800 p-4 gap-2 rounded-lg">
          <button
            className={`p-2 rounded text-sm font-medium transition-colors ${
              activeTab === "exclusion-rules"
                ? "text-black dark:text-white bg-[#77BEF0BF] dark:bg-blue-600"
                : "border-transparent text-[#4A4A4A] dark:text-gray-300 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:text-white"
            }`}
            onClick={() => handleTabChange("exclusion-rules")}
          >
            Exclusion
          </button>
          <button
            className={`p-2 rounded text-sm font-medium transition-colors ${
              activeTab === "inclusion-rules"
                ? "text-black dark:text-white bg-[#77BEF0BF] dark:bg-blue-600"
                : "border-transparent text-[#4A4A4A] dark:text-gray-300 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:text-white"
            }`}
            onClick={() => handleTabChange("inclusion-rules")}
          >
            Inclusion
          </button>
        </div>
        <button
          className="btn btn-sm rounded-full text-sm bg-[#4545FF] hover:bg-blue-700 text-white"
          onClick={handleAddRow}
        >
          Add
        </button>
      </div>

      {loading ? (
        <p className="dark:text-gray-300">Loading...</p>
      ) : (
        <>
          {loading ? (
            <p className="dark:text-gray-300">Loading...</p>
          ) : (
            <div className="w-full overflow-x-auto">
              <div className="min-w-fit max-h-[calc(100vh-300px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                <DataTable
                  columns={columns.map((col) => ({
                    ...col,
                    minWidth: getColumnWidth(col.label),
                  }))}
                  data={tableData}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Modal */}
      {deleteModal.show && (
        <dialog className="modal modal-open">
          <div className="modal-box bg-white dark:bg-gray-800 dark:text-gray-100">
            <h3 className="font-bold text-lg dark:text-gray-100">
              Confirm Delete
            </h3>
            <p className="py-4 dark:text-gray-300">
              Are you sure you want to delete this rule?
            </p>
            <div className="modal-action">
              <button
                className="btn btn-outline dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                onClick={() => setDeleteModal({ show: false, row: null })}
              >
                Cancel
              </button>
              <button
                className="btn btn-error dark:bg-red-600 dark:hover:bg-red-700 dark:text-white"
                onClick={() => handleDelete(deleteModal.row)}
              >
                Delete
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button
              className="cursor-default"
              onClick={() => setDeleteModal({ show: false, row: null })}
            >
              close
            </button>
          </form>
        </dialog>
      )}
    </div>
  );
}

// Helper
function getColumnWidth(label) {
  switch (label) {
    case "Tenant":
      return "210px";
    case "Station Group":
      return "200px";
    case "Station":
      return "180px";
    case "Measurement":
      return "200px";
    case "Status":
      return "120px";
    // case 'Online': return '120px';
    case "Action":
      return "90px";
    default:
      return "120px";
  }
}

export default function InclusionExclusion({ setIsDirty, isDirty }) {
  const [tenants, setTenants] = useState([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const didFetchTenants = useRef(false);

  useEffect(() => {
    if (didFetchTenants.current) return;
    didFetchTenants.current = true;

    const fetchTenants = async () => {
      try {
        setIsLoadingTenants(true);
        const res = await api.get("/config/ddls");
        setTenants(
          [
            {
              created_at: new Date().toISOString(),
              deleted_at: null,
              id: 0,
              is_active: true,
              origo_tenant_id: "0",
              origo_tenant_name: "All Tenants",
              updated_at: "2025-09-21T09:18:07.571Z",
            },
            ...res.data?.data?.tenants,
          ] || []
        );
      } catch (err) {
        console.error("Error fetching tenants:", err);
      } finally {
        setIsLoadingTenants(false);
      }
    };
    fetchTenants();
  }, []);

  return (
    <div className="bg-gray-100 dark:bg-gray-900 gap-6 overflow-auto flex-1">
      <ConfigTable
        tenants={tenants}
        isLoadingTenants={isLoadingTenants}
        setIsDirty={setIsDirty}
        isDirty={isDirty}
      />
    </div>
  );
}
